import { I18n } from "@/i18n/I18n";
import { useState, useEffect } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useProjectStore } from "@/frontend/store/ProjectStore";
import { useShallow } from "zustand/react/shallow";
import { PartType } from "@/backend/access/part/types";
import { Button, Modal, Form } from "react-bootstrap";
import { ConfirmModal } from "../modals/ConfirmModal";
import { PartParams } from "@/backend/access/part/types";
import { CreatePartRequest, CreatePartFromTemplateRequest } from "@/backend/express/controllers/partController";
import { PartInstancesList } from "./PartInstancesList";
import { PartsList } from "./PartsList";
import { useMemo } from "react";
import { NotFound } from "@/frontend/pages/NotFound";
import { ContainersPlaceholder } from "../containers/ContainersPlaceholder";

type PartTab = 'part_instance' | 'parts';

export function ProjectPartsList() {
  const projectId = useParams().projectId;
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const fetchProjectParts = useProjectStore(state => state.fetchProjectParts);
  const createPart = useProjectStore(state => state.createPart);
  const createPartFromTemplate = useProjectStore(state => state.createPartFromTemplate);
  const deletePart = useProjectStore(state => state.deletePart);
  const projectParts = useProjectStore(useShallow(state => 
   state.projectParts.filter(p => p.projectId === projectId)
  ));
  
  const tabFromUrl = searchParams.get('tab') as PartTab | null;
  const [activeTab, setActiveTab] = useState<PartTab>(tabFromUrl === 'parts' ? 'parts' : 'part_instance');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [partToDelete, setPartToDelete] = useState<PartParams | null>(null);
  const [createTitle, setCreateTitle] = useState('');
  const [createDescription, setCreateDescription] = useState('');
  const [createTitleError, setCreateTitleError] = useState('');
  const [selectedPartTemplateId, setSelectedPartTemplateId] = useState<string>('');
  const [loading, setLoading] = useState(true);

  const partTemplates = useMemo(() => {
    return projectParts.filter(part => part.type === PartType.TEMPLATE);
  }, [projectParts]);

  useEffect(() => {
    if (projectId) {
      void fetchProjectParts(projectId).finally(() => {
        setLoading(false);
      });
    }
  }, [projectId, fetchProjectParts]);

  useEffect(() => {
    const tabFromUrl = searchParams.get('tab') as PartTab | null;
    if (tabFromUrl === 'parts' || tabFromUrl === 'part_instance') {
      setActiveTab(tabFromUrl);
    } else if (!tabFromUrl) { 
      void navigate(`/projects/${projectId}/parts?tab=part_instance`, { replace: true });
    }
  }, [searchParams, projectId, navigate]);

  const handleCreatePart = async () => {
    if (!projectId) return;
    
    let hasError = false;
    if (!createTitle.trim()) {
      setCreateTitleError(I18n.get('requiredFieldError', { field: I18n.get('title') }));
      hasError = true;
    }

    if (hasError) {
      return;
    }

    if (activeTab === 'part_instance' && selectedPartTemplateId) {
      const partTemplateData: CreatePartFromTemplateRequest = {
        partTemplateId: selectedPartTemplateId,
        title: createTitle,
        description: createDescription,
      };
      void createPartFromTemplate(partTemplateData);
    } else {
      const partType = activeTab === 'part_instance' ? PartType.PART_INSTANCE : PartType.TEMPLATE;
      const partData: CreatePartRequest = {
        title: createTitle,
        description: createDescription,
        projectId,
        type: partType,
      };
      void createPart(partData);
    }
    
    handleCloseCreateModal();
  };

  const handleCreateClick = (tab: PartTab) => {
    setActiveTab(tab);
    setShowCreateModal(true);
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setCreateTitle('');
    setCreateDescription('');
    setCreateTitleError('');
    setSelectedPartTemplateId('');
  };

  const handlePartTemplateChange = (partTemplateId: string) => {
    setSelectedPartTemplateId(partTemplateId);
    if (partTemplateId) {
      const partTemplate = partTemplates.find(t => t.id === partTemplateId);
      if (partTemplate) {
        setCreateTitle(partTemplate.title);
        setCreateDescription(partTemplate.description);
      }
    } else {
      setCreateTitle('');
      setCreateDescription('');
    }
  };

  const handleDeleteClick = (part: PartParams) => {
    setPartToDelete(part);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!partToDelete || !projectId) return;
    await deletePart(projectId, partToDelete.id);
    setShowDeleteModal(false);
    setPartToDelete(null);
  };

  if (!projectId) {
    return <NotFound />;
  }

  if(loading) {
    return <ContainersPlaceholder />;
  }

  return (
    <div className="p-3">
      <div className="pb-3">
        <div className="d-flex justify-content-between align-items-center">
          <h1>{activeTab === 'parts' ? I18n.get('parts') : I18n.get('partInstances')}</h1>
        </div>
      </div>

      {activeTab === 'part_instance' && projectId && (
        <PartInstancesList
          projectId={projectId}
          onCreateClick={() => handleCreateClick('part_instance')}
          onDelete={handleDeleteClick}
        />
      )}

      {activeTab === 'parts' && projectId && (
        <PartsList
          projectId={projectId}
          onCreateClick={() => handleCreateClick('parts')}
          onDelete={handleDeleteClick}
        />
      )}

      <Modal show={showCreateModal} onHide={handleCloseCreateModal}>
        <Modal.Header closeButton>
          <Modal.Title>{I18n.get('createModel', { model: I18n.get('part') })}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            {activeTab === 'part_instance' && partTemplates.length > 0 && (
              <Form.Group className="mb-3">
                <Form.Label htmlFor="part-template">{`${I18n.get('createFromTemplate')} (${I18n.get('optional')})`}</Form.Label>
                <Form.Select
                  id="part-template"
                  value={selectedPartTemplateId}
                  onChange={(e) => handlePartTemplateChange(e.target.value)}
                >
                  <option value="">{I18n.get('selectTemplate')}</option>
                  {partTemplates.map(partTemplate => (
                    <option key={partTemplate.id} value={partTemplate.id}>
                      {partTemplate.title}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}
            <Form.Group className="mb-3">
              <Form.Label htmlFor="part-title">{I18n.get('title')}</Form.Label>
              <Form.Control
                id="part-title"
                type="text"
                value={createTitle}
                placeholder={I18n.get('enterField', { field: I18n.get('title') })}
                aria-label={I18n.get('enterField', { field: I18n.get('title') })}
                isInvalid={!!createTitleError}
                onChange={(e) => {
                  setCreateTitle(e.target.value);
                  setCreateTitleError('');
                }}
              />
              <Form.Control.Feedback type="invalid">{createTitleError}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label htmlFor="part-description">{`${I18n.get('description')} (${I18n.get('optional')})`}</Form.Label>
              <Form.Control
                id="part-description"
                as="textarea"
                rows={3}
                value={createDescription}
                placeholder={I18n.get('enterField', { field: I18n.get('description') })}
                aria-label={I18n.get('enterField', { field: I18n.get('description') })}
                onChange={(e) => setCreateDescription(e.target.value)}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseCreateModal}>
            {I18n.get('cancel')}
          </Button>
          <Button variant="primary" onClick={() => void handleCreatePart()}>
            {I18n.get('confirm')}
          </Button>
        </Modal.Footer>
      </Modal>

      {showDeleteModal && partToDelete && (
        <ConfirmModal
          title={I18n.get('deleteModel', { model: I18n.get('part'), title: partToDelete.title })}
          confirmMessage={I18n.get('areYouSureDeleteModel', { model: I18n.get('part'), title: partToDelete.title })}
          onConfirm={() => handleDeleteConfirm()}
          setShowModal={setShowDeleteModal}
        />
      )}
    </div>
  );
}
